import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Container, Form, Button, Spinner, Alert, Card, Row, Col } from 'react-bootstrap';
import ReactQuill from 'react-quill';

import VereinbarungService from '../services/vereinbarung.service';
import PlatzhalterAuswahlModal from './PlatzhalterAuswahlModal';

// Auswaehlbare Stufen fuer die Sichtbarkeit (Codes muessen mit Backend-Whitelist
// in app/utils/tierVisibility.js uebereinstimmen).
const TIER_OPTIONS = [
    { code: 'mainvo', label: 'Haupt-VO (MainVo)' },
    { code: 'subvo', label: 'Reseller (SubVo)' },
    { code: 'adm', label: 'Vertriebspartner (ADM)' },
    { code: 'customer', label: 'Kunde' },
];

const KUENDIGUNG_EINHEITEN = ['Tag(e)', 'Woche(n)', 'Monat(e)', 'Jahre'];

// Wiederverwendung des optischen Stils der echten "Neue Vereinbarung erstellen"-Maske.
const styles = {
    pageContainer: {
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        minHeight: '100vh',
        paddingTop: '40px',
        paddingBottom: '60px'
    },
    mainContainer: { maxWidth: '1200px', margin: '0 auto' },
    header: {
        color: '#ffffff', fontSize: '42px', fontWeight: '700', marginBottom: '40px',
        textAlign: 'center', textShadow: '2px 2px 4px rgba(0,0,0,0.1)', letterSpacing: '-0.5px'
    },
    mainCard: {
        borderRadius: '25px', border: 'none', boxShadow: '0 20px 60px rgba(0,0,0,0.1)',
        overflow: 'hidden', background: '#ffffff'
    },
    cardHeader: {
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: '25px 35px', borderBottom: 'none'
    },
    cardBody: { padding: '40px 35px', background: '#ffffff' },
    sectionTitle: {
        fontSize: '18px', fontWeight: '600', color: '#667eea', marginBottom: '25px',
        marginTop: '35px', paddingBottom: '10px', borderBottom: '2px solid #f0f2ff',
        display: 'flex', alignItems: 'center'
    },
    sectionIcon: { marginRight: '10px', fontSize: '20px' },
    formGroup: { marginBottom: '25px' },
    formLabel: { fontWeight: '500', color: '#4a5568', marginBottom: '8px', fontSize: '14px', letterSpacing: '0.3px' },
    requiredStar: { color: '#e53e3e', marginLeft: '4px' },
    formControl: {
        borderRadius: '12px', border: '2px solid #e2e8f0', padding: '12px 16px',
        fontSize: '15px', transition: 'all 0.3s ease', backgroundColor: '#f7fafc'
    },
    formSelect: {
        borderRadius: '12px', border: '2px solid #e2e8f0', padding: '12px 16px',
        fontSize: '15px', transition: 'all 0.3s ease', backgroundColor: '#f7fafc', cursor: 'pointer'
    },
    textArea: {
        borderRadius: '12px', border: '2px solid #e2e8f0', padding: '12px 16px',
        fontSize: '15px', transition: 'all 0.3s ease', backgroundColor: '#f7fafc',
        resize: 'vertical', minHeight: '100px'
    },
    placeholderButton: {
        background: 'linear-gradient(135deg, #ffb347 0%, #ffcc33 100%)', border: 'none',
        borderRadius: '10px', padding: '8px 20px', fontSize: '14px', fontWeight: '500',
        marginBottom: '15px', boxShadow: '0 4px 15px rgba(255, 179, 71, 0.3)', transition: 'all 0.3s ease'
    },
    quillContainer: {
        borderRadius: '12px', overflow: 'hidden', border: '2px solid #e2e8f0', backgroundColor: '#f7fafc'
    },
    tierBox: {
        background: '#f7fafc', border: '2px solid #e2e8f0', borderRadius: '12px', padding: '16px 20px'
    },
    submitButton: {
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', border: 'none',
        borderRadius: '12px', padding: '14px 40px', fontSize: '16px', fontWeight: '600',
        letterSpacing: '0.5px', boxShadow: '0 10px 30px rgba(102, 126, 234, 0.3)',
        transition: 'all 0.3s ease', marginTop: '30px'
    },
    secondaryButton: {
        borderRadius: '12px', padding: '14px 30px', fontSize: '16px', fontWeight: '600', marginTop: '30px'
    },
    alert: { borderRadius: '12px', padding: '16px 20px', marginTop: '20px', marginBottom: '20px', border: 'none', fontSize: '15px' },
    successAlert: { background: 'linear-gradient(135deg, #84fab0 0%, #8fd3f4 100%)', color: '#0c5460' },
    errorAlert: { background: 'linear-gradient(135deg, #fccb90 0%, #ff9a9e 100%)', color: '#721c24' },
    loadingContainer: {
        minHeight: '100vh', display: 'flex', flexDirection: 'column', justifyContent: 'center',
        alignItems: 'center', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
    },
    loadingSpinner: { width: '60px', height: '60px', borderWidth: '4px', borderColor: '#ffffff', borderTopColor: 'transparent' },
    loadingText: { color: '#ffffff', fontSize: '18px', marginTop: '20px', fontWeight: '500' },
    infoBox: { background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', borderRadius: '15px', padding: '20px', marginBottom: '30px', color: '#ffffff' },
    infoText: { margin: '0', fontSize: '14px', lineHeight: '1.6' }
};

const SystemVereinbarungsvorlage = () => {
    const { id } = useParams();
    const isEditMode = !!id;
    const navigate = useNavigate();
    const quillRef = useRef(null);

    const [titel, setTitel] = useState('');
    const [titelKatalogId, setTitelKatalogId] = useState('');
    const [katalogList, setKatalogList] = useState([]);
    const [beschreibung, setBeschreibung] = useState('');
    const [inhalt, setInhalt] = useState('');
    const [bearbeiterVon, setBearbeiterVon] = useState('');
    const [bearbeiterAn, setBearbeiterAn] = useState('');
    const [zusammenfassung, setZusammenfassung] = useState('');
    const [anlagen, setAnlagen] = useState('');
    const [gueltigkeitVon, setGueltigkeitVon] = useState('');
    const [gueltigkeitBis, setGueltigkeitBis] = useState('');
    const [kuendigungsfristWert, setKuendigungsfristWert] = useState('');
    const [kuendigungsfristEinheit, setKuendigungsfristEinheit] = useState('Tag(e)');
    const [visibleTiers, setVisibleTiers] = useState([]);

    const [loading, setLoading] = useState(isEditMode);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [modalShow, setModalShow] = useState(false);

    // System-Titel-Katalog laden (Titel werden hieraus ausgewaehlt, kein Freitext).
    useEffect(() => {
        VereinbarungService.getTitleCatalog('system')
            .then((res) => setKatalogList(Array.isArray(res.data) ? res.data : []))
            .catch(() => setKatalogList([]));
    }, []);

    useEffect(() => {
        if (!isEditMode) return;
        setLoading(true);
        VereinbarungService.getAgreementTitleById(id)
            .then((res) => {
                const d = res.data || {};
                setTitel(d.titel || '');
                setTitelKatalogId(d.titel_katalog_id != null ? String(d.titel_katalog_id) : '');
                setBeschreibung(d.beschreibung || '');
                setInhalt(d.inhalt_vorlage || '');
                setVisibleTiers(Array.isArray(d.visible_tiers) ? d.visible_tiers : []);
                const def = d.vorlage_defaults || {};
                setZusammenfassung(def.kurze_zusammenfassung || '');
                setBearbeiterVon(def.bearbeiter_von || '');
                setBearbeiterAn(def.bearbeiter_an || '');
                setAnlagen(def.anlagen || '');
                setGueltigkeitVon(def.gueltigkeit_von || '');
                setGueltigkeitBis(def.gueltigkeit_bis || '');
                setKuendigungsfristWert(def.kuendigungsfrist_wert != null ? String(def.kuendigungsfrist_wert) : '');
                setKuendigungsfristEinheit(def.kuendigungsfrist_einheit || 'Tag(e)');
                setLoading(false);
            })
            .catch(() => {
                setError('Fehler beim Laden der Vorlage.');
                setLoading(false);
            });
    }, [id, isEditMode]);

    const handleSelectPlaceholder = (placeholderKey) => {
        const editor = quillRef.current.getEditor();
        const range = editor.getSelection(true);
        editor.insertText(range.index, placeholderKey, 'user');
    };

    const toggleTier = (code) => {
        setVisibleTiers((prev) => prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]);
    };

    // Titel wird ausschliesslich aus dem System-Katalog gewaehlt (kein Freitext).
    const handleKatalogChange = (e) => {
        const value = e.target.value;
        setTitelKatalogId(value);
        const entry = katalogList.find((k) => String(k.katalog_id) === String(value));
        setTitel(entry ? entry.bezeichnung : '');
    };

    const handleSave = (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        // Einzige Pflicht der System-Vorlage: ein Titel aus dem Katalog.
        // Die uebrige Muss-Logik greift erst in den zugewiesenen Stufen beim
        // Erstellen einer echten Vereinbarung (siehe VereinbarungErstellen.js).
        if (!titelKatalogId) {
            setError('Bitte waehlen Sie einen Vereinbarungstitel aus dem Katalog.');
            return;
        }

        setSubmitting(true);

        const payload = {
            titel,
            titel_katalog_id: titelKatalogId,
            beschreibung,
            inhalt_vorlage: inhalt,
            is_system: true,
            visible_tiers: visibleTiers,
            vorlage_defaults: {
                kurze_zusammenfassung: zusammenfassung || '',
                bearbeiter_von: bearbeiterVon || '',
                bearbeiter_an: bearbeiterAn || '',
                anlagen: anlagen || '',
                gueltigkeit_von: gueltigkeitVon || '',
                gueltigkeit_bis: gueltigkeitBis || '',
                kuendigungsfrist_wert: kuendigungsfristWert || '',
                kuendigungsfrist_einheit: kuendigungsfristWert ? kuendigungsfristEinheit : ''
            }
        };

        const promise = isEditMode
            ? VereinbarungService.updateAgreementTitle(id, payload)
            : VereinbarungService.createAgreementTitle(payload);

        promise
            .then(() => {
                setSuccess('System-Vorlage erfolgreich gespeichert! Sie werden weitergeleitet...');
                setTimeout(() => navigate('/system-agreement-templates'), 1500);
            })
            .catch((err) => {
                const msg = (err.response && err.response.data && err.response.data.message) || 'Ein unbekannter Fehler ist aufgetreten.';
                setError(msg);
                setSubmitting(false);
            });
    };

    if (loading) {
        return (
            <div style={styles.loadingContainer}>
                <Spinner animation="border" style={styles.loadingSpinner} />
                <p style={styles.loadingText}>Lade Vorlage...</p>
            </div>
        );
    }

    return (
        <div style={styles.pageContainer}>
            <Container style={styles.mainContainer}>
                <h1 style={styles.header}>
                    {isEditMode ? '✏️ System-Vereinbarungsvorlage bearbeiten' : '✨ Neue System-Vereinbarungsvorlage'}
                </h1>

                <Card style={styles.mainCard}>
                    <div style={styles.cardHeader}>
                        <div style={styles.infoBox}>
                            <p style={styles.infoText}>
                                📋 Diese Vorlage wird oberhalb der Stufen gespeichert. Definieren Sie die Default-Werte
                                und legen Sie unten fest, in welchen Stufen die Vorlage sichtbar wird. Für die System-Vorlage
                                sind keine Felder Pflicht – die Muss-Logik greift erst in den zugewiesenen Stufen.
                            </p>
                        </div>
                    </div>

                    <Card.Body style={styles.cardBody}>
                        <Form onSubmit={handleSave}>
                            {/* Hauptinformationen */}
                            <div style={styles.sectionTitle}>
                                <span style={styles.sectionIcon}>📝</span>
                                Hauptinformationen
                            </div>

                            <Row>
                                <Col md={6}>
                                    <Form.Group style={styles.formGroup} controlId="formTitel">
                                        <Form.Label style={styles.formLabel}>
                                            Vereinbarungstitel
                                            <span style={styles.requiredStar}>*</span>
                                        </Form.Label>
                                        <Form.Select
                                            style={styles.formSelect}
                                            value={titelKatalogId}
                                            onChange={handleKatalogChange}
                                            required
                                        >
                                            <option value="">Bitte Titel aus Katalog waehlen</option>
                                            {katalogList.map((k) => (
                                                <option key={k.katalog_id} value={k.katalog_id}>{k.bezeichnung}</option>
                                            ))}
                                        </Form.Select>
                                        <Form.Text className="text-muted">
                                            Titel werden zentral unter „Stammdaten → Vereinbarungs-Titel" gepflegt.
                                        </Form.Text>
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group style={styles.formGroup} controlId="formBeschreibung">
                                        <Form.Label style={styles.formLabel}>Beschreibung (intern)</Form.Label>
                                        <Form.Control
                                            style={styles.formControl}
                                            type="text"
                                            value={beschreibung}
                                            onChange={(e) => setBeschreibung(e.target.value)}
                                            placeholder="Kurzbeschreibung der Vorlage..."
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>

                            {/* Bearbeiter (Default-Werte) */}
                            <div style={styles.sectionTitle}>
                                <span style={styles.sectionIcon}>👥</span>
                                Bearbeiter (Standard-Vorbelegung)
                            </div>

                            <Row>
                                <Col md={6}>
                                    <Form.Group style={styles.formGroup} controlId="formBearbeiterVon">
                                        <Form.Label style={styles.formLabel}>Bearbeiter (Von)</Form.Label>
                                        <Form.Control
                                            style={styles.formControl}
                                            type="text"
                                            value={bearbeiterVon}
                                            onChange={(e) => setBearbeiterVon(e.target.value)}
                                            placeholder="Name eingeben..."
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group style={styles.formGroup} controlId="formBearbeiterAn">
                                        <Form.Label style={styles.formLabel}>Bearbeiter (An)</Form.Label>
                                        <Form.Control
                                            style={styles.formControl}
                                            type="text"
                                            value={bearbeiterAn}
                                            onChange={(e) => setBearbeiterAn(e.target.value)}
                                            placeholder="Name eingeben..."
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>

                            {/* Gültigkeitszeitraum (Default-Werte) */}
                            <div style={styles.sectionTitle}>
                                <span style={styles.sectionIcon}>📅</span>
                                Gültigkeitszeitraum (Standard-Vorbelegung)
                            </div>

                            <Row>
                                <Col md={6}>
                                    <Form.Group style={styles.formGroup} controlId="formGueltigVon">
                                        <Form.Label style={styles.formLabel}>Gültig von</Form.Label>
                                        <Form.Control
                                            style={styles.formControl}
                                            type="date"
                                            value={gueltigkeitVon}
                                            onChange={(e) => setGueltigkeitVon(e.target.value)}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group style={styles.formGroup} controlId="formGueltigBis">
                                        <Form.Label style={styles.formLabel}>Gültig bis</Form.Label>
                                        <Form.Control
                                            style={styles.formControl}
                                            type="date"
                                            value={gueltigkeitBis}
                                            onChange={(e) => setGueltigkeitBis(e.target.value)}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>

                            {/* Inhalt */}
                            <div style={styles.sectionTitle}>
                                <span style={styles.sectionIcon}>📄</span>
                                Vereinbarungsinhalt
                            </div>

                            <Form.Group style={styles.formGroup} controlId="formZusammenfassung">
                                <Form.Label style={styles.formLabel}>Kurze Zusammenfassung</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={2}
                                    style={styles.textArea}
                                    value={zusammenfassung}
                                    onChange={(e) => setZusammenfassung(e.target.value)}
                                    placeholder="Geben Sie eine kurze Zusammenfassung ein..."
                                />
                            </Form.Group>

                            <Form.Group style={styles.formGroup} controlId="formInhalt">
                                <Form.Label style={styles.formLabel}>
                                    Inhalt der Vereinbarung
                                </Form.Label>
                                <div>
                                    <Button
                                        variant="warning"
                                        size="sm"
                                        style={styles.placeholderButton}
                                        onClick={() => setModalShow(true)}
                                        type="button"
                                    >
                                        🏷️ Platzhalter einfügen
                                    </Button>
                                </div>
                                <div style={styles.quillContainer}>
                                    <ReactQuill
                                        ref={quillRef}
                                        theme="snow"
                                        value={inhalt}
                                        onChange={setInhalt}
                                        className="resizable-editor"
                                        style={{ minHeight: '300px' }}
                                    />
                                </div>
                            </Form.Group>

                            {/* Zusätzliche Informationen (Default-Werte) */}
                            <div style={styles.sectionTitle}>
                                <span style={styles.sectionIcon}>📎</span>
                                Zusätzliche Informationen (Standard-Vorbelegung)
                            </div>

                            <Form.Group style={styles.formGroup} controlId="formAnlagen">
                                <Form.Label style={styles.formLabel}>Anlagen</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    style={styles.textArea}
                                    value={anlagen}
                                    onChange={(e) => setAnlagen(e.target.value)}
                                    placeholder="Eine Anlage pro Zeile..."
                                />
                            </Form.Group>

                            <Form.Group as={Row} style={styles.formGroup} controlId="formKuendigung">
                                <Form.Label column sm={2} style={styles.formLabel}>⏰ Kündigungsfrist</Form.Label>
                                <Col sm={5}>
                                    <Form.Control
                                        style={styles.formControl}
                                        type="number"
                                        value={kuendigungsfristWert}
                                        onChange={(e) => setKuendigungsfristWert(e.target.value)}
                                        placeholder="z.B. 3"
                                    />
                                </Col>
                                <Col sm={5}>
                                    <Form.Select
                                        style={styles.formSelect}
                                        value={kuendigungsfristEinheit}
                                        onChange={(e) => setKuendigungsfristEinheit(e.target.value)}
                                    >
                                        {KUENDIGUNG_EINHEITEN.map((u) => <option key={u}>{u}</option>)}
                                    </Form.Select>
                                </Col>
                            </Form.Group>

                            {/* Stufen-Sichtbarkeit */}
                            <div style={styles.sectionTitle}>
                                <span style={styles.sectionIcon}>👁️</span>
                                Sichtbar in Stufe(n)
                            </div>

                            <div style={styles.tierBox}>
                                <Row>
                                    {TIER_OPTIONS.map((opt) => (
                                        <Col xs={12} sm={6} md={3} key={opt.code}>
                                            <Form.Check
                                                type="checkbox"
                                                id={`tier-${opt.code}`}
                                                label={opt.label}
                                                checked={visibleTiers.includes(opt.code)}
                                                onChange={() => toggleTier(opt.code)}
                                            />
                                        </Col>
                                    ))}
                                </Row>
                                <Form.Text className="text-muted">
                                    Die Vorlage erscheint nur in den hier ausgewählten Stufen als wiederverwendbarer Default.
                                </Form.Text>
                            </div>

                            {error && <Alert style={{ ...styles.alert, ...styles.errorAlert }}>⚠️ {error}</Alert>}
                            {success && <Alert style={{ ...styles.alert, ...styles.successAlert }}>✅ {success}</Alert>}

                            <div style={{ textAlign: 'center', display: 'flex', gap: '15px', justifyContent: 'center' }}>
                                <Button
                                    variant="light"
                                    type="button"
                                    style={styles.secondaryButton}
                                    onClick={() => navigate('/system-agreement-templates')}
                                >
                                    Abbrechen
                                </Button>
                                <Button variant="primary" type="submit" disabled={submitting} style={styles.submitButton}>
                                    {submitting
                                        ? (<><Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" style={{ marginRight: '10px' }} />Speichert...</>)
                                        : (<>💾 Vorlage speichern</>)}
                                </Button>
                            </div>
                        </Form>
                    </Card.Body>
                </Card>
            </Container>

            <PlatzhalterAuswahlModal
                show={modalShow}
                onHide={() => setModalShow(false)}
                onSelectPlaceholder={handleSelectPlaceholder}
            />
        </div>
    );
};

export default SystemVereinbarungsvorlage;
